import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/shared/ui/button.jsx";
import { InstitutionalHeader } from "./components/InstitutionalHeader.jsx";

/**
 * Páginas legais (Privacidade e Termos).
 *
 * As duas compartilham a mesma moldura e diferem só pelo conteúdo, que fica
 * declarado em `TEXTOS_LEGAIS`. No app antigo eram dois arquivos com o
 * mesmo layout copiado.
 */

const TEXTOS_LEGAIS = {
  privacidade: {
    title: "Política de Privacidade",
    updated: "Atualizada em agosto de 2026",
    sections: [
      {
        title: "1. Quais dados coletamos",
        body: [
          "Dados de cadastro da clínica: razão social, nome fantasia, CNPJ, endereço e dados de contato do responsável.",
          "Dados dos usuários do sistema: nome, e-mail e perfil de acesso. As senhas são armazenadas apenas como hash criptográfico — nem nós conseguimos lê-las.",
          "Dados dos leads e pacientes que a clínica cadastra: nome, telefone, origem, procedimento de interesse, anotações e histórico de conversas.",
        ],
      },
      {
        title: "2. Como usamos esses dados",
        body: [
          "Para operar o sistema: exibir o funil comercial, enviar e receber mensagens no WhatsApp da clínica, agendar retornos e gerar relatórios.",
          "Para suporte técnico, quando a clínica solicita ajuda.",
          "Não vendemos, alugamos nem compartilhamos dados de pacientes com terceiros para fins de marketing.",
        ],
      },
      {
        title: "3. Isolamento entre clínicas",
        body: [
          "Cada clínica é um ambiente separado. Toda consulta ao banco filtra pelo identificador da clínica no servidor, e não existe caminho que devolva o dado de uma clínica para outra.",
          "Quando um membro da nossa equipe precisa acessar o ambiente de um cliente para prestar suporte, o acesso é registrado na trilha de auditoria, com data, hora e responsável.",
        ],
      },
      {
        title: "4. Segurança",
        body: [
          "Senhas com hash bcrypt e sessões em cookie httpOnly, que o JavaScript da página não consegue ler.",
          "Credenciais de integração (como o token do WhatsApp) são cifradas com AES-256-GCM e nunca são enviadas de volta ao navegador.",
          "Eventos recebidos do WhatsApp têm a assinatura conferida antes de serem aceitos.",
        ],
      },
      {
        title: "5. Seus direitos (LGPD)",
        body: [
          "A clínica é a controladora dos dados dos seus pacientes; a OdontoPlug atua como operadora.",
          "Titulares podem solicitar acesso, correção ou exclusão dos seus dados diretamente à clínica, que tem as ferramentas para atender ao pedido dentro do sistema.",
          "Para pedidos relacionados aos dados da própria clínica, escreva para contato@odontoplug.com.br.",
        ],
      },
      {
        title: "6. Retenção",
        body: [
          "Os dados permanecem enquanto o contrato estiver ativo. No encerramento, a clínica pode solicitar a exportação; após o prazo acordado, os dados são removidos definitivamente.",
        ],
      },
    ],
  },

  termos: {
    title: "Termos de Uso",
    updated: "Atualizados em agosto de 2026",
    sections: [
      {
        title: "1. Objeto",
        body: [
          "Estes termos regem o uso da plataforma OdontoPlug, um sistema de gestão e relacionamento para clínicas odontológicas, disponibilizado no modelo de assinatura mensal.",
        ],
      },
      {
        title: "2. Contratação e acesso",
        body: [
          "O acesso é concedido à clínica contratante, que administra os próprios usuários e é responsável pelas credenciais distribuídas à sua equipe.",
          "Cada pessoa deve usar credenciais individuais. O compartilhamento de senha é vedado e pode comprometer a trilha de auditoria.",
        ],
      },
      {
        title: "3. Responsabilidades da clínica",
        body: [
          "Garantir que possui base legal para tratar os dados dos pacientes cadastrados.",
          "Respeitar as políticas do WhatsApp Business ao usar a integração com a Meta, incluindo as regras sobre janelas de atendimento e modelos de mensagem.",
          "Manter os dados cadastrais e de cobrança atualizados.",
        ],
      },
      {
        title: "4. Responsabilidades da OdontoPlug",
        body: [
          "Manter a plataforma disponível, aplicar as medidas de segurança descritas na Política de Privacidade e prestar suporte nos canais informados.",
          "Comunicar com antecedência mudanças relevantes de funcionalidade ou de preço.",
        ],
      },
      {
        title: "5. Pagamento e renovação",
        body: [
          "A assinatura é mensal e renovada automaticamente. O atraso no pagamento pode levar à suspensão do acesso após comunicação prévia.",
          "O cancelamento pode ser solicitado a qualquer momento e vale a partir do ciclo seguinte.",
        ],
      },
      {
        title: "6. Integrações de terceiros",
        body: [
          "A integração com a WhatsApp Cloud API depende de serviços da Meta. Indisponibilidades, mudanças de política ou bloqueios impostos por ela estão fora do nosso controle.",
        ],
      },
      {
        title: "7. Foro",
        body: ["Fica eleito o foro da comarca de Itajaí/SC para dirimir questões destes termos."],
      },
    ],
  },
};

/** @param {{ documento: "privacidade" | "termos" }} props */
export function LegalPage({ documento }) {
  const conteudo = TEXTOS_LEGAIS[documento];

  return (
    <div className="min-h-screen bg-background">
      <InstitutionalHeader />

      <main className="container mx-auto max-w-3xl px-4 py-16">
        <Button asChild variant="ghost" size="sm" className="mb-6 gap-1.5">
          <Link to="/">
            <ArrowLeft className="h-4 w-4" />
            Voltar ao início
          </Link>
        </Button>

        <h1 className="font-display text-3xl font-bold md:text-4xl">{conteudo.title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{conteudo.updated}</p>

        <div className="mt-10 space-y-10">
          {conteudo.sections.map((secao) => (
            <section key={secao.title}>
              <h2 className="font-display text-lg font-semibold">{secao.title}</h2>
              <div className="mt-3 space-y-3">
                {secao.body.map((paragrafo, i) => (
                  <p key={i} className="text-sm leading-relaxed text-muted-foreground">
                    {paragrafo}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <p className="mt-12 border-t border-border pt-6 text-xs text-muted-foreground">
          Dúvidas sobre este documento? Escreva para{" "}
          <a href="mailto:contato@odontoplug.com.br" className="text-primary hover:underline">
            contato@odontoplug.com.br
          </a>
          .
        </p>
      </main>
    </div>
  );
}
